import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Crown, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
  </label>
);

const AppSumoRedeem = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const errorParam = searchParams.get('error');

  const [mode, setMode] = useState('signup'); // 'signup' | 'login'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(errorParam || '');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!code) {
      setError('Missing AppSumo activation code. Please return to AppSumo and click "Activate now" again.');
    }
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code) return;
    setSubmitting(true);
    setError('');
    try {
      const body = mode === 'signup'
        ? { code, email, password, name }
        : { code, email, login_password: loginPassword };
      const response = await axios.post(`${API}/appsumo/oauth/exchange`, body);
      const { token, user, tier, license_key } = response.data;
      localStorage.setItem('token', token);
      setSuccess({ tier, license_key, email: user?.email });
      // Hard reload so AuthProvider picks up the new token cleanly
      setTimeout(() => { window.location.href = '/app/dashboard'; }, 1800);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to redeem AppSumo license';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 max-w-lg w-full text-center" data-testid="appsumo-success">
          <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lifetime deal activated!</h1>
          <p className="text-gray-600 mb-6">
            Your URL Monitor Pro <span className="font-semibold capitalize">{success.tier}</span> lifetime plan is now active on <span className="font-medium">{success.email}</span>. Redirecting to your dashboard…
          </p>
          <div className="text-xs text-gray-400 break-all">License: {success.license_key}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-blue-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 max-w-lg w-full">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-amber-100 p-3 rounded-xl">
            <Crown className="w-7 h-7 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Claim your AppSumo Lifetime deal</h1>
            <p className="text-sm text-gray-600">URL Monitor Pro · Lifetime Business</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 flex items-start space-x-2 text-sm" data-testid="appsumo-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex bg-gray-100 rounded-lg p-1 mb-5">
          <button
            type="button"
            onClick={() => setMode('signup')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${mode === 'signup' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
            data-testid="appsumo-mode-signup"
          >
            I'm new — create account
          </button>
          <button
            type="button"
            onClick={() => setMode('login')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${mode === 'login' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
            data-testid="appsumo-mode-login"
          >
            I already have an account
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Email">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="you@company.com"
              data-testid="appsumo-email"
            />
          </Field>

          {mode === 'signup' ? (
            <>
              <Field label="Your name">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Jane Doe"
                  data-testid="appsumo-name"
                />
              </Field>
              <Field label="Create a password" hint="Minimum 8 characters">
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  data-testid="appsumo-password"
                />
              </Field>
            </>
          ) : (
            <Field label="Your URL Monitor Pro password">
              <input
                type="password"
                required
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                data-testid="appsumo-login-password"
              />
            </Field>
          )}

          <button
            type="submit"
            disabled={submitting || !code}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
            data-testid="appsumo-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{mode === 'signup' ? 'Activate Lifetime Business' : 'Attach license to my account'}</span>
          </button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-6">
          By activating you agree to our <Link to="/terms" className="text-blue-600 hover:underline">Terms</Link> and <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>. AppSumo's 60-day refund policy applies.
        </p>
      </div>
    </div>
  );
};

export default AppSumoRedeem;
